import React from 'react';
import { ImagesIcon } from '@phosphor-icons/react';
import Image from '@plone/volto/components/theme/Image/Image';
import { withBlockExtensions } from '@plone/volto/helpers';
import {
  MPFBlockCardHeader,
  MPFCardCarousel,
  MPFLink,
} from 'volto-ministerio-publico-federal/components/UI';
import { EditItemButton } from 'volto-ministerio-publico-federal/components/View/CapaView/EditItemButton';

import {
  getSubHomeGaleriaImageProps,
  SUBHOME_GALERIA_MAX_ITEMS,
  SUBHOME_GALERIA_MIN_ITEMS,
  SUBHOME_GALERIA_SLIDES,
  SUBHOME_GALERIA_TITLE,
  toSubHomeGaleriaCards,
  type SubHomeGaleriaCard,
  type SubHomeGaleriaData,
} from './helpers';

interface ViewProps {
  data: SubHomeGaleriaData;
  block: string;
  isEditMode?: boolean;
  /** Presente só no editor: abre a imagem de índice N na barra lateral. */
  onEditItem?: (index: number) => void;
}

const IMAGE_CLASS =
  'absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none';

/* `not-prose` desliga a tipografia de texto corrido que a página herda do
   editor (`[class*='__editor-content']`, em `base/_editor-content.scss`):
   as faixas trazem a própria tipografia, e sem isto o `prose` recolore e
   redimensiona títulos, links e listas de dentro do cartão. Mesmo recurso
   que `mpfNoticiasPorTema` e `mpfCampanhas` já usam. */
const ROOT_CLASS = 'mpf-subhome-card not-prose';

const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mpf-subhome-card__placeholder">{children}</p>
);

/**
 * Faixa "Carrossel Imagens" da sub-home (bloco `mpfSubHomeGaleria`).
 *
 * Diferente do `mpfSubHomeCarrossel`, aqui não há card de texto ao lado: a
 * faixa é só a prateleira de imagens, ocupando a largura toda do cartão. Cada
 * imagem aceita legenda e destino, como pede a anotação do Figma.
 */
export const MpfSubHomeGaleriaBlockView: React.FC<ViewProps> = ({
  data,
  block,
  isEditMode,
  onEditItem,
}) => {
  const titleId = `subhome-galeria-${block}`;
  const cards = React.useMemo(
    () => toSubHomeGaleriaCards(data?.items),
    [data?.items],
  );
  const overflowCount = (data?.items?.length ?? 0) - SUBHOME_GALERIA_MAX_ITEMS;

  const header = (
    <MPFBlockCardHeader
      icon={
        <ImagesIcon
          aria-hidden="true"
          className="h-6 w-6 shrink-0 text-[color:var(--orange-600)]"
        />
      }
      title={data?.title || SUBHOME_GALERIA_TITLE}
      titleId={titleId}
      ruleClassName="bg-[color:var(--orange-600)]"
    />
  );

  if (cards.length === 0) {
    return isEditMode ? (
      <section className={ROOT_CLASS}>
        {header}
        <Notice>
          Escolha as imagens da faixa na barra lateral — de{' '}
          {SUBHOME_GALERIA_MIN_ITEMS} a {SUBHOME_GALERIA_MAX_ITEMS}.
        </Notice>
      </section>
    ) : null;
  }

  return (
    <section aria-labelledby={titleId} className={ROOT_CLASS}>
      {header}

      <MPFCardCarousel
        className="w-full"
        name={`subhome-galeria-${block}`}
        breakpoints={{
          640: { slidesPerView: 2, spaceBetween: 16 },
          1024: { slidesPerView: SUBHOME_GALERIA_SLIDES, spaceBetween: 16 },
        }}
        labels={{
          prev: 'Imagens anteriores',
          next: 'Próximas imagens',
          first: 'Esta é a primeira imagem',
          last: 'Esta é a última imagem',
          bullet: 'Ir para a imagem {{index}}',
        }}
        slides={cards.map((card) => ({
          key: card.key,
          content: (
            <GaleriaCard
              card={card}
              isEditMode={isEditMode}
              onEditItem={onEditItem}
            />
          ),
        }))}
      />

      {isEditMode && cards.length < SUBHOME_GALERIA_MIN_ITEMS && (
        <Notice>
          A faixa foi desenhada para pelo menos {SUBHOME_GALERIA_MIN_ITEMS}{' '}
          imagens. Com menos, o carrossel fica com espaço sobrando no desktop.
        </Notice>
      )}

      {isEditMode && overflowCount > 0 && (
        <Notice>
          {overflowCount === 1
            ? `A última imagem não aparece: a faixa exibe no máximo ${SUBHOME_GALERIA_MAX_ITEMS}.`
            : `As últimas ${overflowCount} imagens não aparecem: a faixa exibe no máximo ${SUBHOME_GALERIA_MAX_ITEMS}.`}
        </Notice>
      )}
    </section>
  );
};

/**
 * Card da galeria: a foto no formato do design (329x288, ou seja ~8/7) e a
 * legenda logo abaixo, quando houver.
 *
 * Com destino preenchido, o card inteiro vira link — o `after:absolute
 * after:inset-0` sobre a legenda estende a área clicável sem inchar o nome
 * acessível do link. Sem legenda, o link envolve a foto e é nomeado pelo
 * texto alternativo dela.
 */
const GaleriaCard: React.FC<{
  card: SubHomeGaleriaCard;
  isEditMode?: boolean;
  onEditItem?: (index: number) => void;
}> = ({ card, isEditMode, onEditItem }) => {
  const imageProps = getSubHomeGaleriaImageProps(card);
  const linkable = !!card.href && !isEditMode;

  const media = (
    /* `var(--outline-var)` na mão, e não a utility `border-outline-variant`:
       o `tailwind.config.js` a aponta para `--outline-variant`, var que os
       tokens nunca emitem (o nome real é `--outline-var`), e a borda cairia
       em `currentColor` — o mesmo tropeço já anotado em `_internal-view.scss`. */
    <div className="relative aspect-[8/7] overflow-hidden rounded-md border border-[color:var(--outline-var)] bg-surface-bright shadow-center dark:border-night-850 dark:bg-night-700">
      {imageProps && (
        <Image
          {...imageProps}
          alt={card.alt}
          loading="lazy"
          className={IMAGE_CLASS}
        />
      )}
    </div>
  );

  return (
    <figure className="group relative m-0 min-w-0">
      {onEditItem && (
        <EditItemButton
          label={`Editar imagem: ${card.caption || card.alt || 'sem legenda'}`}
          onClick={() => onEditItem(card.sourceIndex)}
          className="absolute right-2 top-2 z-10"
        />
      )}

      {linkable && !card.caption ? (
        <MPFLink
          href={card.href}
          openInNewTab={card.external || undefined}
          className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {media}
        </MPFLink>
      ) : (
        media
      )}

      {card.caption && (
        <figcaption className="mt-2 line-clamp-2 text-base font-medium text-on-surface dark:text-night-100">
          {linkable ? (
            <MPFLink
              href={card.href}
              openInNewTab={card.external || undefined}
              className="after:absolute after:inset-0 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              {card.caption}
            </MPFLink>
          ) : (
            card.caption
          )}
        </figcaption>
      )}
    </figure>
  );
};

export default withBlockExtensions(MpfSubHomeGaleriaBlockView);
